<template>
    <UnitDetailLayout
        kind="sector"
        :entity="sector || {}"
        :collaborators="collaborators"
        :roles="roles"
        :course-rows="courseRows"
        :completion-avg="completionAvg"
        :loading="loading"
        :error="error"
        :error-message="error === 'not_found' ? t.sectorDetail.notFound : t.common.error"
        :breadcrumbs="breadcrumbs"
        :title="sector?.name || t.sectorDetail.title"
        :subtitle="t.sectorDetail.pageLead"
        :labels="layoutLabels"
        :role-label-fn="roleLabel"
        @back="$router.push('/dashboard/corporativo/estrutura')"
        @open-collaborator="goCollaborator"
        @open-role="goRole"
    />
</template>

<script>
import UnitDetailLayout from '@/components/Admin/Corporate/UnitDetailLayout.vue';
import organizationalUnitsApi from '@/utils/corporate/organizationalUnits';
import usersApi from '@/utils/corporate/users';
import { aggregateCourses } from '@/utils/corporate/progressAggregate';
import { corporateI18n } from '@/i18n/mixins';
import { mapState } from 'vuex';

export default {
    name: 'CorporateSectorDetail',
    mixins: [corporateI18n],
    components: { UnitDetailLayout },
    data() {
        return {
            loading: true,
            error: false,
            sector: null,
            roles: [],
            collaborators: [],
            courseRows: [],
            stats: null,
        };
    },
    computed: {
        ...mapState(['token']),
        completionAvg() {
            if (this.stats && typeof this.stats.completionAvg === 'number') {
                return this.stats.completionAvg;
            }
            if (!this.collaborators.length) return 0;
            const sum = this.collaborators.reduce(
                (acc, u) => acc + (Number(u.progressPercent) || 0),
                0,
            );
            return Math.round(sum / this.collaborators.length);
        },
        breadcrumbs() {
            return [
                { label: this.t.structure.title, to: '/dashboard/corporativo/estrutura' },
                { label: this.t.overview.sectors },
                { label: this.sector?.name || this.t.sectorDetail.title },
            ];
        },
        layoutLabels() {
            const d = this.t.sectorDetail;
            const s = this.t.structure;
            const c = this.t.collaborators;
            return {
                back: d.backShort,
                overviewTab: d.overviewTab,
                collaboratorsTab: d.collaborators,
                rolesTab: d.statRoles,
                coursesTab: d.coursesTab,
                active: s.active,
                inactive: s.inactive,
                manager: d.manager,
                sector: this.t.roleDetail.sector,
                peopleWord: d.peopleWord,
                peopleWordSingular: d.peopleWordSingular,
                rolesWord: d.rolesWord,
                rolesWordSingular: d.rolesWordSingular,
                coursesWord: d.coursesWord,
                coursesWordSingular: d.coursesWordSingular,
                activePlural: d.activePlural,
                activeSingular: d.activeSingular,
                progressSuffix: d.progressSuffix,
                avgCompletion: d.statCompletion,
                withProgress: d.withProgress,
                filterActive: c.active,
                filterInactive: c.inactive,
                filterAll: this.t.common.filterAll,
                inactiveRole: d.inactiveRole,
                inactiveRoles: d.inactiveRoles,
                allRolesActive: d.allRolesActive,
                noCourses: d.noCourses,
                assignedCourses: d.statCoursesAssigned,
                sectorProgress: d.sectorProgress,
                roleProgress: d.sectorProgress,
                inProgress: d.inProgress,
                completed: d.completed,
                overdue: d.overdue,
                performance: d.performance,
                performanceRole: d.performance,
                overallProgress: d.overallProgress,
                notStarted: d.notStarted,
                attention: d.attention,
                attentionLead: d.attentionLead,
                attentionEmpty: d.attentionEmpty,
                seeDetails: d.seeDetails,
                seeAll: d.seeAll,
                searchPeople: d.searchPeople,
                allRoles: d.allRoles,
                roleCol: c.role,
                statusCol: c.status,
                progressCol: c.progress,
                peopleEmptyTitle: d.collaboratorsEmpty,
                rolesEmptyTitle: d.rolesEmptyTitle,
                rolesEmptyLead: d.rolesEmptyLead,
                coursesEmptyTitle: d.coursesEmptyTitle,
                coursesEmptyLead: d.coursesEmptyLead,
                courseStatusDone: this.t.detail.statusCompleted,
                courseStatusProgress: this.t.detail.statusInProgress,
                courseStatusIdle: this.t.detail.statusNotStarted,
            };
        },
    },
    watch: {
        '$route.params.id': {
            immediate: true,
            handler() {
                this.load();
            },
        },
    },
    methods: {
        roleLabel(user) {
            if (user?.roleName) return user.roleName;
            const isSectorManager = this.sector?.managerUserId
                && String(this.sector.managerUserId) === String(user?.id);
            if (isSectorManager || user?.isResponsible) {
                return this.t.sectorDetail.manager;
            }
            return '—';
        },
        goRole(id) {
            this.$router.push({
                path: `/dashboard/corporativo/cargos/${id}`,
                query: { from: 'setor' },
            });
        },
        goCollaborator(id) {
            this.$router.push(`/dashboard/corporativo/colaboradores/${id}`);
        },
        async load() {
            const id = this.$route.params.id;
            if (!id) return;
            this.loading = true;
            this.error = false;
            try {
                const [sector, usersRes, stats] = await Promise.all([
                    organizationalUnitsApi.getById(this.token, id),
                    usersApi.list(this.token, { sectorId: id, size: 500 }),
                    organizationalUnitsApi.getStats(this.token, id).catch(() => null),
                ]);

                if (!sector || (sector.type && sector.type !== 'sector')) {
                    this.error = 'not_found';
                    return;
                }

                this.sector = sector;
                this.collaborators = usersRes.content || [];
                this.stats = stats;
                this.roles = Array.isArray(stats?.roles) ? stats.roles : [];
                this.courseRows = Array.isArray(stats?.courses)
                    ? stats.courses
                    : aggregateCourses(this.collaborators);
            } catch (e) {
                console.error(e);
                this.error = e?.code === 'NOT_FOUND' ? 'not_found' : true;
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>
